import React from 'react';
import { Box, Button, CircularProgress, DialogContent, Divider, Stack } from '@mui/material';
import { Iconify, NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import { CustomDialogTitle } from 'apps/t2c-fe/Common/dialog';
import { MediaStatus, MediaStatusType } from 'apps/t2c-fe/utils';
import { Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useRouter } from 'next/router';
import { BrollCaptionVideo, Status } from '../../@types';
import CustomizedProgressBars from '../../Common/ProgressBar';
import { ProgressStuckNote } from '../../Common';

interface UploadedModalProps {
  onCloseDialog: () => void;
  status: string | undefined;
  overallPercentage: number;
  handleCloseModal: () => void;
  uploadId: string;
  handleOnRetry: () => void;
  isUploadError: boolean;
  isRetryDisable: boolean;
}

export const UploadedModal: React.FC<UploadedModalProps> = ({
  uploadId,
  onCloseDialog,
  overallPercentage,
  status,
  handleCloseModal,
  handleOnRetry,
  isUploadError,
  isRetryDisable,
}) => {
  const { push } = useRouter();
  const handleNext = () => {
    push(`/edit-video/${uploadId}`);
  };
  return (
    <>
      <CustomDialogTitle
        title={
          status === Status.Error
            ? 'Failed!'
            : status === Status.Success
            ? 'Video Ready!'
            : 'Processing Your Video'
        }
        onClose={onCloseDialog}
        sx={styles.title}
      />

      <DialogContent sx={styles.dialogContent}>
        {((!status && !isUploadError) ||
          status === Status.CompressingVideo ||
          status === Status.ExtractingAudio ||
          status === Status.ExtractingCaption ||
          status === Status.GeneratingBroll ||
          status === Status.ApplyingAIBroll ||
          status === Status.NotStarted) && (
          <Box sx={styles.container}>
            <Typography sx={styles.heading}>
              {status === Status.CompressingVideo || status === Status.ExtractingAudio
                ? 'Analyzing the video structure and visual elements.'
                : status === Status.ExtractingCaption
                ? 'AI generating accurate captions.'
                : status === Status.GeneratingBroll
                ? 'AI analyzing the video to identify highlight-worthy sections.'
                : status === Status.ApplyingAIBroll
                ? 'Applying broll clips to video'
                : 'Uploading video...'}
            </Typography>
            <CircularProgress />
            <Stack justifyContent={'center'} alignItems={'center'} width={'100%'} rowGap={0.5}>
              <CustomizedProgressBars value={overallPercentage} sx={{ width: '80%' }} />
              {/* <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Typography>Estimated Time:</Typography>
                <Typography fontWeight={'600'}>15 Minutes</Typography>
              </Stack> */}
            </Stack>
            {status && status !== Status.NotStarted ? (
              <>
                <Typography
                  textAlign={'center'}
                  sx={{ 
                    color: (theme) => theme.palette.text.placeholder, 
                    width: '82%' 
                  }}
                >
                  Your video is currently being processed, which may take a few minutes. You can stay on
                  this screen to proceed when it's ready, <b> or minimize this window</b> and continue
                  from the dashboard later.
                </Typography>
                <ProgressStuckNote/>
              </>
            ) : (
              <Typography
                  textAlign={'center'}
                  sx={{ color: (theme) => theme.palette.text.placeholder, width: '82%' }}
                >
                  Uploading your file... almost there! 🚀 Hang tight for a moment.
                </Typography>
            )}
          </Box>
        )}
        {status === Status.Success && (
          <Box sx={styles.container}>
            <Typography sx={styles.heading}>Successfully Processed!</Typography>
            {/* <Icon icon="qlementine-icons:success-16" width="16" height="16" /> */}
            <NextImage src={'/assets/icons/VideoSuccessIcon.svg'} height={75} width={75} alt="" />
            <Typography>
              Your video has been successfully processed. You can proceed to the next screen or
              minimize this window to review it from the dashboard at your convenience.
            </Typography>
          </Box>
        )}
        {(status === Status.Error || isUploadError) && (
          <Box sx={styles.container}>
            <Typography sx={styles.heading}>Oops!</Typography>
            <ErrorOutlineIcon sx={{ width: 75, height: 75 }} color="error" />
            <Typography sx={{ color: (theme) => theme.palette.text.error }}>
              {isUploadError
                ? 'File upload was unsuccessful. Please try again.'
                : 'We hit a glitch in the matrix! Your video didn’t make it. Give it another shot!'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: !status && !isUploadError ? 'flex-end' : 'space-between',
          px: 3,
          py: 1,
        }}
      >
        {!status || status === Status.Error || isUploadError ? (
          <>
            {(status === Status.Error || isUploadError) && (
              <Button
                variant="contained"
                fullWidth
                sx={{ ...styles.submitBtn, color: (theme) => theme.palette.common.white }}
                onClick={handleOnRetry}
                disabled={isRetryDisable}
              >
                Retry
              </Button>
            )}

            <Button
              variant="outlined"
              fullWidth
              sx={{ ...styles.submitBtn, color: (theme) => theme.palette.primary.main }}
              onClick={handleCloseModal}
            >
              {status === Status.Error ? 'Close' : 'Cancel'}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              fullWidth
              sx={styles.submitBtn}
              onClick={onCloseDialog}
              disabled={status === Status.NotStarted}
            >
              Minimize
            </Button>

            <Button
              variant="contained"
              fullWidth
              sx={styles.submitBtn}
              disabled={status !== Status.Success}
              onClick={handleNext}
            >
              Next
            </Button>
          </>
        )}
      </Box>
    </>
  );
};

const styles: Styles = {
  submitBtn: {
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    py: 2.5,
    maxWidth: '200px',
    margin: 1,
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: 3,
    textAlign: 'center',
  },
  heading: { fontSize: '26px', fontWeight: 600 },
  dialogContent: {
    backgroundColor: (theme)=> theme.palette.background.secondary
  }
};
